import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../../config';
import { useCurrency } from '../../utils/currency';
import { useI18n } from '../../i18n/I18nContext';
import './ComparateurQuartiers.css';

function ComparateurQuartiers() {
  const { t } = useI18n();
  const [quartiers, setQuartiers] = useState([]);
  const [selected, setSelected] = useState(['', '']);
  const [resultat, setResultat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/tools/quartiers`)
      .then(res => res.json())
      .then(data => setQuartiers(data.quartiers || []))
      .catch(() => {});
  }, []);

  const { formatPrix } = useCurrency();

  const addQuartier = () => {
    if (selected.length < 3) {
      setSelected([...selected, '']);
    }
  };

  const removeQuartier = (index) => {
    if (selected.length > 2) {
      setSelected(selected.filter((_, i) => i !== index));
    }
  };

  const updateSelected = (index, value) => {
    const newSelected = [...selected];
    newSelected[index] = value;
    setSelected(newSelected);
  };

  const comparer = async () => {
    const valides = selected.filter(Boolean);
    if (valides.length < 2) return;

    setLoading(true);
    setError(null);
    setResultat(null);

    try {
      const res = await fetch(`${API_URL}/tools/comparateur?quartiers=${valides.join(',')}`);
      const data = await res.json();
      setResultat(data.quartiers);
    } catch {
      setError(t.tools.comparError);
    } finally {
      setLoading(false);
    }
  };

  // Trouver le max pour calculer les barres proportionnelles
  const getBarWidth = (value, metric) => {
    if (!resultat || value === 0) return 0;
    const values = resultat.map(q => q[metric]).filter(v => v > 0);
    const max = Math.max(...values);
    return max > 0 ? (value / max) * 100 : 0;
  };

  // Trouver la meilleure valeur (minimum pour prix, maximum pour surface/annonces)
  const isBest = (value, metric) => {
    if (!resultat || value === 0) return false;
    const values = resultat.map(q => q[metric]).filter(v => v > 0);
    if (values.length === 0) return false;
    if (metric === 'prixMoyen' || metric === 'prixM2Moyen') {
      return value === Math.min(...values);
    }
    return value === Math.max(...values);
  };

  const METRICS = [
    { key: 'nbAnnonces', label: t.tools.nbAnnonces, format: v => v },
    { key: 'prixMoyen', label: t.tools.avgPrice, format: formatPrix },
    { key: 'prixMin', label: t.tools.minPrice, format: formatPrix },
    { key: 'prixMax', label: t.tools.maxPrice, format: formatPrix },
    { key: 'surfaceMoyenne', label: t.tools.avgSurface, format: v => v + ' m²' },
    { key: 'prixM2Moyen', label: t.tools.avgPriceM2, format: formatPrix }
  ];

  return (
    <div className="comparateur-page">
      <Link to="/outils" className="btn btn-secondary">{t.tools.backToTools}</Link>

      <h1>{t.tools.comparTitle}</h1>
      <p className="comp-subtitle">{t.tools.comparDesc}</p>

      <div className="comp-selection glass-card">
        <div className="comp-selects">
          {selected.map((val, i) => (
            <div key={i} className="comp-select-item">
              <select
                value={val}
                onChange={e => updateSelected(i, e.target.value)}
              >
                <option value="">{t.tools.quartierLabel} {i + 1}</option>
                {quartiers
                  .filter(q => q === val || !selected.includes(q))
                  .map(q => (
                    <option key={q} value={q}>{q}</option>
                  ))}
              </select>
              {selected.length > 2 && (
                <button className="comp-remove" onClick={() => removeQuartier(i)}>
                  &times;
                </button>
              )}
            </div>
          ))}
          {selected.length < 3 && (
            <button className="btn btn-secondary comp-add" onClick={addQuartier}>
              {t.tools.addDistrict}
            </button>
          )}
        </div>
        <button
          className="btn btn-primary"
          onClick={comparer}
          disabled={selected.filter(Boolean).length < 2 || loading}
        >
          {loading ? t.tools.comparLoading : t.tools.compareBtn}
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      {resultat && (
        <div className="comp-results glass-card">
          {/* Header avec noms des quartiers */}
          <div className="comp-table-header" style={{ gridTemplateColumns: `200px repeat(${resultat.length}, 1fr)` }}>
            <div className="comp-metric-label"></div>
            {resultat.map(q => (
              <div key={q.nom} className="comp-quartier-name">{q.nom}</div>
            ))}
          </div>

          {/* Types disponibles */}
          <div className="comp-row comp-types-row" style={{ gridTemplateColumns: `200px repeat(${resultat.length}, 1fr)` }}>
            <div className="comp-metric-label">{t.tools.availableTypes}</div>
            {resultat.map(q => (
              <div key={q.nom} className="comp-cell">
                <div className="comp-types">
                  {q.typesDisponibles.length > 0
                    ? q.typesDisponibles.map(tp => (
                        <span key={tp} className="comp-type-tag">{tp}</span>
                      ))
                    : <span className="comp-no-data">-</span>
                  }
                </div>
              </div>
            ))}
          </div>

          {/* Metriques avec barres */}
          {METRICS.map(metric => (
            <div
              key={metric.key}
              className="comp-row"
              style={{ gridTemplateColumns: `200px repeat(${resultat.length}, 1fr)` }}
            >
              <div className="comp-metric-label">{metric.label}</div>
              {resultat.map(q => (
                <div key={q.nom} className="comp-cell">
                  <div className={`comp-value ${isBest(q[metric.key], metric.key) ? 'best' : ''}`}>
                    {q[metric.key] > 0 ? metric.format(q[metric.key]) : '-'}
                  </div>
                  {q[metric.key] > 0 && (
                    <div className="comp-bar-container">
                      <div
                        className={`comp-bar-fill ${isBest(q[metric.key], metric.key) ? 'best' : ''}`}
                        style={{ width: `${getBarWidth(q[metric.key], metric.key)}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ComparateurQuartiers;
